import { useCallback, useState, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";
import { readRegistrationStatus, requestPasswordReset } from "../../api/accountsApi";
import ErrorNotice from "../../errors/ErrorNotice";
import { useLoad } from "../../shared/loading/useLoad";
import { useSubmission } from "../../shared/state/useSubmission";
import AppButton from "../../ui/buttons/AppButton";
import StatusMessage from "../../ui/feedback/StatusMessage";
import { FORM_CONTROL_CLASS } from "../../ui/styles/formStyles";
import AuthCardPage from "./AuthCardPage";
import BackToSignInLink from "./BackToSignInLink";
import Field from "../../ui/forms/Field";

/** `/forgot`: ask for a password reset link (Q-36). The answer is the same for every email. */
export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const { state, submit } = useSubmission();
  const loadStatus = useCallback((signal: AbortSignal) => readRegistrationStatus(signal), []);
  const { state: status } = useLoad(loadStatus);

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit(() => requestPasswordReset(email));
  };

  return (
    <AuthCardPage title={t("forgot.title")}>
      {status.status === "loaded" && !status.value.emailDelivery ? (
        <StatusMessage tone="info" className="mb-4">{t("forgot.noEmailDelivery")}</StatusMessage>
      ) : null}
      <form onSubmit={handleSubmit}>
        <Field id="forgot-email" label={t("forgot.email")}>
          {(control) => (
            <input
              {...control}
              type="email"
              className={`mb-3 ${FORM_CONTROL_CLASS}`}
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              autoComplete="email"
              required
            />
          )}
        </Field>
        <AppButton type="submit" disabled={state.status === "submitting"} className="w-full" variant="primary">
          {state.status === "submitting" ? t("login.pleaseWait") : t("forgot.requestLink")}
        </AppButton>
        {state.status === "succeeded" ? (
          <StatusMessage tone="success" className="mt-3">{t("forgot.sent")}</StatusMessage>
        ) : null}
        {state.status === "failed" ? <ErrorNotice error={state.error} className="mt-3" /> : null}
        <BackToSignInLink />
      </form>
    </AuthCardPage>
  );
}
